import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, 
  Search, 
  User, 
  Home,
  History,
  Users,
  Scroll,
  ScrollText,
  Brain,
  GraduationCap,
  Building2,
  ChevronDown,
  HandCoins,
  Sun as SunIcon,
  Clock,
  Compass,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { GlobalSearchBar } from './GlobalSearchBar';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useAssignmentBadges } from '../context/AssignmentBadgesContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { buildDrawerSections } from './mobileNavSections';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Top-level navigation. Items with `subItems` open as a hover dropdown
// to keep the bar compact while exposing sub-pages (Coran → Lire/Mémoriser,
// Compagnons → Liste/Histoires, Prophètes → liste + Muhammad ﷺ).
const navItems = [
  { path: '/', tk: 'nav.home', icon: Home },
  {
    path: '/quran', tk: 'nav.quran', icon: BookOpen,
    subItems: [
      { path: '/quran',        labelFr: 'Lire',     icon: BookOpen },
      { path: '/memorization', labelFr: 'Mémoriser', icon: Brain },
    ],
  },
  { path: '/cours', tk: 'nav.cours', icon: GraduationCap },
  { path: '/hadith', tk: 'nav.hadith', icon: ScrollText },
  {
    path: '/prophets', tk: 'nav.prophets', icon: Scroll,
    subItems: [
      { path: '/prophets',         labelFr: 'Liste des prophètes', icon: Scroll },
      { path: '/prophet-timeline', labelFr: 'Muhammad ﷺ — Sīra',     icon: History },
    ],
  },
  {
    path: '/companions', tk: 'nav.companions', icon: Users,
    subItems: [
      { path: '/companions',         labelFr: 'Liste',     icon: Users },
      { path: '/companions/stories', labelFr: 'Histoires', icon: BookOpen },
    ],
  },
  {
    path: '/prieres/horaires', tk: 'nav.prieres', icon: SunIcon,
    fallbackLabel: 'Prières',
    subItems: [
      { path: '/prieres/horaires', labelFr: 'Horaires', icon: Clock },
      { path: '/prieres/qibla',    labelFr: 'Qibla',    icon: Compass },
    ],
  },
  { path: '/dons', tk: 'nav.dons', icon: HandCoins, fallbackLabel: 'Dons' },
];

const NavBadge = () => {
  const { receivedUnseenCount, submittedUnseenCount } = useAssignmentBadges();
  const total = (receivedUnseenCount || 0) + (submittedUnseenCount || 0);
  if (!total) return null;
  return (
    <span
      className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500/90 text-white text-[10px] font-semibold"
      data-testid="nav-devoirs-badge"
    >
      {total > 99 ? '99+' : total}
    </span>
  );
};

const NavTopItem = ({ item, isActive, t }) => {
  if (!item.subItems) {
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.tk.split('.').pop()}`}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
          isActive
            ? 'text-primary bg-primary/10'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface'
        }`}
      >
        <item.icon className="w-4 h-4" />
        {t(item.tk)}
      </Link>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`nav-${item.tk.split('.').pop()}-trigger`}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
            isActive
              ? 'text-primary bg-primary/10'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface'
          }`}
        >
          <item.icon className="w-4 h-4" />
          {t(item.tk)}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-background/95 backdrop-blur-xl border border-white/10 min-w-[200px]">
        {item.subItems.map((sub) => (
          <DropdownMenuItem key={sub.path} asChild>
            <Link
              to={sub.path}
              data-testid={`nav-${item.tk.split('.').pop()}-sub-${sub.path.split('/').filter(Boolean).join('-') || 'root'}`}
              className="flex items-center gap-2 cursor-pointer"
            >
              <sub.icon className="w-4 h-4" />
              {sub.labelFr}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Navbar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [memberships, setMemberships] = React.useState([]);
  const [searchOpen, setSearchOpen] = React.useState(false);

  // Fetch memberships once when authenticated so the user dropdown can
  // build role-aware sections that mirror the mobile drawer.
  React.useEffect(() => {
    if (!user) { setMemberships([]); return; }
    const tk = localStorage.getItem('noor_albayan_token');
    axios.get(`${API_URL}/api/madrassas/me`, {
      headers: tk ? { Authorization: `Bearer ${tk}` } : {},
    })
      .then((r) => setMemberships(r.data?.items || []))
      .catch(() => setMemberships([]));
  }, [user]);

  const userSections = user ? buildDrawerSections({ user, memberships }) : [];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/70 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Main Row */}
        <div className="flex items-center justify-between h-16">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <img
              src="/logo-192.png"
              alt="Noor El Bayen"
              className="w-10 h-10 min-w-[40px] min-h-[40px] rounded-lg shrink-0 bg-primary/20 p-0.5"
              draggable="false"
            />
            <h1 className="hidden sm:block text-lg font-serif text-text-primary whitespace-nowrap" dir="rtl">
              نور البيان
            </h1>
          </Link>

          {/* Center - Navigation Menu (Desktop) */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
                || (item.path !== '/' && location.pathname.startsWith(item.path))
                || (item.subItems || []).some((s) =>
                  location.pathname === s.path
                  || (s.path !== '/' && location.pathname.startsWith(s.path)));
              return (
                <NavTopItem key={item.path} item={item} isActive={isActive} t={t} />
              );
            })}
          </nav>

          {/* Right side - Search + User Menu */}
          <div className="flex items-center gap-3">
            {/* Search button - opens modal on desktop, nav on mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              className="text-text-secondary hover:text-text-primary"
              aria-label="Recherche"
              data-testid="search-button"
            >
              <Search className="w-5 h-5" />
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="rounded-full"
                    data-testid="user-menu-button"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-72 max-h-[80vh] overflow-y-auto bg-surface border-border"
                  data-testid="user-menu-content"
                >
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-text-primary truncate">{user.name || user.email}</p>
                    <p className="text-xs text-text-muted truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-border" />

                  {userSections.map((section, sIdx) => {
                    // Skip the always-flat "Découverte" section: its items are
                    // already exposed in the top-level nav bar above.
                    if (!section.title) return null;
                    const Icon = section.icon;
                    const slug = section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    return (
                      <React.Fragment key={sIdx}>
                        {sIdx > 0 && <DropdownMenuSeparator className="bg-border" />}
                        <DropdownMenuLabel
                          className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-text-muted font-semibold"
                          data-testid={`user-menu-section-${slug}`}
                        >
                          {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
                          {section.title}
                        </DropdownMenuLabel>
                        {section.items.map((it) => (
                          <DropdownMenuItem key={it.path} asChild>
                            <Link
                              to={it.path}
                              className="flex items-center gap-2 cursor-pointer"
                              data-testid={`user-menu-link-${it.path.replace(/[^a-z0-9]/gi, '-').replace(/^-|-$/g, '')}`}
                            >
                              {it.icon ? <it.icon className="w-4 h-4 opacity-80" /> : null}
                              <span className="truncate">{it.label}</span>
                              {it.path === '/devoirs?tab=received' && <NavBadge />}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-danger cursor-pointer"
                    data-testid="logout-button"
                  >
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-primary/50 text-primary hover:bg-primary/10"
                  data-testid="login-button"
                >
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu - Second Row */}
        <div className="md:hidden border-t border-border bg-surface/50 backdrop-blur-sm">
          <nav className="flex flex-wrap items-center gap-2 px-0 py-2 overflow-x-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
                || (item.path !== '/' && location.pathname.startsWith(item.path))
                || (item.subItems || []).some((s) =>
                  location.pathname === s.path
                  || (s.path !== '/' && location.pathname.startsWith(s.path)));
              return (
                <NavTopItem key={item.path} item={item} isActive={isActive} t={t} />
              );
            })}
          </nav>
        </div>
      </div>
    </header>

    {/* Search Modal Overlay */}
    {searchOpen && (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        onClick={() => setSearchOpen(false)}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 top-16"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="pt-6 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-2xl mx-auto">
            <GlobalSearchBar autoFocus onSelectResult={() => setSearchOpen(false)} />
          </div>
        </div>
      </motion.div>
    )}
  </>
  );
};

const NavbarWithSearchModal = () => {
  const [searchOpen, setSearchOpen] = React.useState(false);
  
  return (
    <>
      <Navbar searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
      {searchOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          onClick={() => setSearchOpen(false)}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 top-16"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="pt-6 px-4 sm:px-6 lg:px-8"
          >
            <div className="max-w-2xl mx-auto">
              <GlobalSearchBar autoFocus onNavigate={() => setSearchOpen(false)} />
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  return (
  <footer className="bg-surface border-t border-border mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo-192.png" alt="Noor El Bayen" className="w-10 h-10 rounded-lg" draggable="false" />
            <div>
              <h3 className="text-lg font-serif text-text-primary">نور البيان</h3>
            </div>
          </div>
          <p className="text-sm text-text-secondary max-w-md">
            {t('footer.tagline')}
          </p>
        </div>
        
        <div>
          <h4 className="font-medium text-text-primary mb-4">{t('footer.study')}</h4>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li><Link to="/quran" className="hover:text-primary transition-colors">{t('nav.quran')}</Link></li>
            <li><Link to="/cours" className="hover:text-primary transition-colors">Apprendre</Link></li>
            <li><Link to="/hadith" className="hover:text-primary transition-colors">Hadith</Link></li>
            <li><Link to="/lexical/search" className="hover:text-primary transition-colors">Analyse lexicale</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-medium text-text-primary mb-4">{t('footer.resources')}</h4>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li><Link to="/prophet-timeline" className="hover:text-primary transition-colors">{t('nav.prophet_timeline')}</Link></li>
            <li><Link to="/companions" className="hover:text-primary transition-colors">{t('nav.companions')}</Link></li>
            <li><Link to="/prieres" className="hover:text-primary transition-colors">Horaires de prière</Link></li>
            <li><Link to="/search" className="hover:text-primary transition-colors">{t('nav.search')}</Link></li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-border mt-8 pt-8 text-center text-sm text-text-muted">
        <p>© {new Date().getFullYear()} Noor El Bayen. {t('footer.copyright_suffix')}</p>
      </div>
    </div>
  </footer>
  );
};

import { MobileShell } from './MobileShell';
import { useIsMobile } from '../hooks/useIsMobile';

export const Layout = ({ children }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col">
        <MobileShell>
          {children}
        </MobileShell>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16">{children}</main>
      <Footer />
    </div>
  );
};
